import { useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Mail, Search, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react'

interface FetchLog {
  id: number
  gmailMessageId: string
  fromEmail: string | null
  subject: string | null
  receivedAt: string | null
  fetchedAt: string
  outcome: 'processed' | 'skipped'
  skipReason: string | null
  skipReasonDisplay: string | null
  emailId: number | null
  gmailUrl: string
}

interface Account {
  id: number
  name: string
  email: string | null
}

interface EmailsProps {
  currentUser?: {
    name: string
    email: string
    avatarUrl?: string
  }
  accounts: Account[]
  selectedAccountId: number | null
  fetchLogs: FetchLog[]
  filters: {
    search: string
    dateRange: string
    outcome: string
  }
  pagination: {
    page: number
    perPage: number
    totalCount: number
    totalPages: number
  }
}

export default function Emails({
  currentUser,
  accounts,
  selectedAccountId,
  fetchLogs,
  filters,
  pagination
}: EmailsProps) {
  const [search, setSearch] = useState(filters.search)
  const [dateRange, setDateRange] = useState(filters.dateRange)
  const [outcome, setOutcome] = useState(filters.outcome)
  const [accountId, setAccountId] = useState(selectedAccountId?.toString() || '')

  const handleSearch = () => {
    router.get('/admin/emails', {
      account_id: accountId,
      search: search,
      date_range: dateRange,
      outcome: outcome,
      page: 1
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handleAccountChange = (newAccountId: string) => {
    setAccountId(newAccountId)
    router.get('/admin/emails', {
      account_id: newAccountId,
      search: search,
      date_range: dateRange,
      outcome: outcome,
      page: 1
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handleDateRangeChange = (newDateRange: string) => {
    setDateRange(newDateRange)
    router.get('/admin/emails', {
      account_id: accountId,
      search: search,
      date_range: newDateRange,
      outcome: outcome,
      page: 1
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handleOutcomeChange = (newOutcome: string) => {
    setOutcome(newOutcome)
    router.get('/admin/emails', {
      account_id: accountId,
      search: search,
      date_range: dateRange,
      outcome: newOutcome,
      page: 1
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handlePageChange = (newPage: number) => {
    router.get('/admin/emails', {
      account_id: accountId,
      search: search,
      date_range: dateRange,
      outcome: outcome,
      page: newPage
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString()
  }

  const truncate = (str: string | null, maxLen: number) => {
    if (!str) return '-'
    if (str.length <= maxLen) return str
    return str.substring(0, maxLen) + '...'
  }

  return (
    <>
      <Head title="Email Inspector - Admin" />
      <AdminLayout currentUser={currentUser}>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-blue-950 dark:text-white mb-2">
              Email Inspector
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Search and inspect all emails fetched from Gmail
            </p>
          </div>

          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg text-gray-900 dark:text-gray-100">
                <Search className="w-5 h-5" />
                Search & Filter
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                {/* Account Dropdown */}
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                    Account
                  </label>
                  <Select value={accountId} onValueChange={handleAccountChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts.map(account => (
                        <SelectItem key={account.id} value={account.id.toString()}>
                          {account.name} {account.email && `(${account.email})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date Range */}
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                    Date Range
                  </label>
                  <Select value={dateRange} onValueChange={handleDateRangeChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="8h">Past 8 hours</SelectItem>
                      <SelectItem value="24h">Past 24 hours</SelectItem>
                      <SelectItem value="48h">Past 48 hours</SelectItem>
                      <SelectItem value="7d">Past 7 days</SelectItem>
                      <SelectItem value="30d">Past 30 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Outcome Filter */}
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                    Status
                  </label>
                  <Select value={outcome} onValueChange={handleOutcomeChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="processed">Processed</SelectItem>
                      <SelectItem value="skipped">Skipped</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Search Input */}
                <div className="lg:col-span-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 block">
                    Search (Gmail ID, From, Subject)
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search..."
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <Button onClick={handleSearch}>
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg text-gray-900 dark:text-gray-100">
                <Mail className="w-5 h-5" />
                Fetch Logs
              </CardTitle>
              <CardDescription>
                {pagination.totalCount} result{pagination.totalCount !== 1 ? 's' : ''} found
              </CardDescription>
            </CardHeader>
            <CardContent>
              {fetchLogs.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No emails found matching your search criteria
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">
                          Status
                        </th>
                        <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">
                          From
                        </th>
                        <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">
                          Subject
                        </th>
                        <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">
                          Fetched
                        </th>
                        <th className="text-left py-3 px-2 font-medium text-gray-600 dark:text-gray-400">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {fetchLogs.map((log) => (
                        <tr
                          key={log.id}
                          className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                        >
                          <td className="py-3 px-2">
                            {log.outcome === 'processed' ? (
                              <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                Processed
                              </Badge>
                            ) : (
                              <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                                {log.skipReasonDisplay || 'Skipped'}
                              </Badge>
                            )}
                          </td>
                          <td className="py-3 px-2 text-gray-900 dark:text-gray-100">
                            {truncate(log.fromEmail, 30)}
                          </td>
                          <td className="py-3 px-2 text-gray-900 dark:text-gray-100">
                            {truncate(log.subject, 40)}
                          </td>
                          <td className="py-3 px-2 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                            {formatDate(log.fetchedAt)}
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                asChild
                              >
                                <Link href={`/admin/emails/${log.id}`}>
                                  View
                                </Link>
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                asChild
                              >
                                <a
                                  href={log.gmailUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Page {pagination.page} of {pagination.totalPages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.totalPages}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    </>
  )
}
